/**
 * @fileoverview Generic pill-style toggle group.
 * @description E.g. scoping a ranking chart to All / Top N / Bottom N so it stays readable as the
 * underlying list grows past what fits legibly in one view. Ported from the InsightBox Banking
 * design system's own `SegmentedControl`, generalized (no banking-specific coupling) since any
 * page needing a small toggle-button group can reuse it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

interface SegmentedControlOption<T extends string> {
  value: T
  label: string
}

interface SegmentedControlProps<T extends string> {
  value: T
  onChange: (value: T) => void
  options: SegmentedControlOption<T>[]
  disabled?: boolean
  'aria-label'?: string
  'data-testid'?: string
}

/**
 * @component
 * @template T
 * @param {SegmentedControlProps<T>} props - The current value, options, and change handler.
 * @param {T} props.value - The currently-selected option's value.
 * @param {(value: T) => void} props.onChange - Called with the newly-selected value.
 * @param {SegmentedControlOption<T>[]} props.options - The toggle options, in display order.
 * @param {boolean} [props.disabled] - Grays the whole group out and blocks selection — e.g. a
 * filter that only makes sense once a related control is set a certain way. Off by default.
 * @param {string} [props['aria-label']] - Accessible name for the whole group.
 * @param {string} [props['data-testid']] - QA locator for the group; each option button gets the
 * same id with its value appended (e.g. capabilities-filter-all).
 */
function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  disabled = false,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-testid={testId}
      className={`flex items-center gap-0.5 rounded-full bg-input-bg p-0.5 ${disabled ? 'opacity-50' : ''}`}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          disabled={disabled}
          data-testid={testId ? `${testId}-${option.value}` : undefined}
          onClick={() => onChange(option.value)}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
            disabled
              ? 'cursor-not-allowed text-label'
              : value === option.value
                ? 'bg-accent text-white shadow-sm'
                : 'text-label hover:text-text'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

export default SegmentedControl
